import { Component } from '@angular/core';
import { NgbActiveModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { QuillEditorComponent, QuillModule } from 'ngx-quill';
import { DIALOG_MODE_IDENTIFIER, IoniconsOutline } from '@pt/pt-models';
import { AsyncPipe, NgIf, NgStyle } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { IonicModule } from '@ionic/angular';
import { CodemirrorModule } from '@ctrl/ngx-codemirror';
import { InlineSuccessDirective } from '@pt/directives';

export enum HelpMode {
  TOOLTIP = 'TOOLTIP',
  DIALOG = 'DIALOG',
}

@Component({
  selector: 'pt-configure-help',
  standalone: true,
  template: `
    <div class="modal-header">
      <h4 class="modal-title">
        {{ title | async }}
      </h4>
    </div>

    <div class="modal-body quill-container">
      <!--CHANGE MODE CONFIRM-->
      <div
        class="change-mode-dialog"
        *ngIf="changeModePending && enableHtmlMode"
      >
        <p>{{ 'MESSAGE.HELP_SWITCH_MODE_MESSAGE' | translate }}.</p>
        <div>
          <button
            (click)="abortSwitchMode()"
            class="btn btn-sm btn-outline-secondary"
          >
            {{ 'BUTTON.CANCEL' | translate }}
          </button>
          <button
            (click)="confirmSwitchMode()"
            class="btn btn-sm btn-primary ms-1"
          >
            {{ 'BUTTON.OK' | translate }}
          </button>
        </div>
      </div>
      <!--END / CHANGE MODE CONFIRM-->

      <!--SWITCH MODE-->
      <div class="h-100">
        <div class="help-editor-header" *ngIf="enableHtmlMode">
          <div class="float-start">
            <div class="d-flex flex-row justify-content-between">
              <div class="d-flex flex-column mb-2">
                <label class="m-0 form-label">{{
                  'LABELS.MODE' | translate
                }}</label>
                <select
                  [(ngModel)]="currentHelpMode"
                  id="helpMode"
                  style="width: 150px"
                  (change)="onHelpModeChange($event)"
                  class="form-control form-control-sm"
                >
                  <option value="DIALOG">
                    {{ 'LABELS.HELP_DIALOG_MODE' | translate }}
                  </option>
                  <option value="TOOLTIP">
                    {{ 'LABELS.HELP_TOOLTIP_MODE' | translate }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div
            class="float-end d-flex"
            style="margin-top: 20px"
            *ngIf="currentHelpMode === HelpMode.DIALOG"
          >
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm d-flex me-2"
              [disabled]="changeModePending"
              ptInlineSuccess
              (click)="copyRawData()"
            >
              <ion-icon
                name="{{ copyIcon }}"
                class="ion-icon hstack me-1"
                [ngStyle]="{ 'font-size': '14px' }"
              ></ion-icon>
              {{ 'LABELS.COPY_RAW_DATA' | translate }}
            </button>

            <button
              type="button"
              class="btn btn-primary btn-sm d-flex"
              *ngIf="dialogEditorType.wysiwyg"
              [disabled]="changeModePending"
              [ngbTooltip]="
                'TOOLTIP.HELP_SWITCH_TO_HTML_EDITOR_MODE' | translate
              "
              (click)="changeDialogEditorType(false, true)"
            >
              <ion-icon
                name="{{ codeIcon }}"
                class="ion-icon hstack me-1"
                [ngStyle]="{ 'font-size': '14px' }"
              ></ion-icon>
              {{ 'LABELS.SIMPLE' | translate }}
            </button>
            <button
              type="button"
              class="btn btn-primary btn-sm d-flex"
              *ngIf="dialogEditorType.custom"
              [ngbTooltip]="'TOOLTIP.HELP_SWITCH_TO_WYSIWYG_MODE' | translate"
              [disabled]="changeModePending"
              (click)="changeDialogEditorType(true, false)"
            >
              <ion-icon
                name="{{ textIcon }}"
                class="ion-icon me-1"
                [ngStyle]="{ 'font-size': '14px' }"
              ></ion-icon>
              {{ 'LABELS.ADVANCED' | translate }}
            </button>
          </div>
        </div>
        <!--END / SWITCH MODE-->

        <form
          [formGroup]="formGroup"
          *ngIf="!changeModePending"
          [style.height]="enableHtmlMode ? 'calc(100% - 50px)' : '100%'"
        >
          <ng-container *ngIf="currentHelpMode === HelpMode.DIALOG">
            <div class="dialog-editor">
              <quill-editor
                class="w-100 h-100"
                *ngIf="dialogEditorType.wysiwyg"
                formControlName="help"
                [modules]="quillModules"
              >
              </quill-editor>

              <ngx-codemirror
                *ngIf="dialogEditorType.custom"
                [preserveScrollPosition]="true"
                formControlName="help"
                [options]="codeMirrorOptions"
              ></ngx-codemirror>
            </div>
          </ng-container>

          <ng-container *ngIf="currentHelpMode === HelpMode.TOOLTIP">
            <textarea
              autocomplete="off"
              formControlName="help"
              class="form-control form-control-sm help-editor-textarea h-100"
              [rows]="10"
            >
            </textarea>
          </ng-container>
        </form>
      </div>
    </div>

    <div class="modal-footer">
      <button
        type="button"
        class="btn btn-primary btn-sm"
        [disabled]="changeModePending"
        (click)="confirm(); $event.stopPropagation()"
      >
        {{ 'BUTTON.CONFIRM' | translate }}
      </button>
      <button
        ngbAutofocus
        type="button"
        [disabled]="changeModePending"
        class="btn btn-outline-secondary btn-sm"
        (click)="activeModal.close(false); $event.stopPropagation()"
      >
        {{ 'BUTTON.CANCEL' | translate }}
      </button>
    </div>
  `,
  imports: [
    AsyncPipe,
    TranslateModule,
    NgIf,
    FormsModule,
    IonicModule,
    NgStyle,
    NgbTooltip,
    ReactiveFormsModule,
    QuillEditorComponent,
    CodemirrorModule,
    InlineSuccessDirective,
  ],
  styles: [
    `
      .quill-container {
        height: 350px !important;
        overflow: hidden;
      }

      .change-mode-dialog {
        background: #adb5bd55;
        flex-direction: column;
        height: 100%;
        position: absolute;
        width: 100%;
        margin: -16px;
        text-align: center;
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 45;
      }

      .dialog-editor {
        height: 228px;
        width: 100%;
        padding-top: 5px;
      }

      .help-editor-textarea {
        /*height: 264px;*/
        /*max-height: 264px;*/
        /*min-height: 264px;*/
        resize: none;
        padding: 7px 8px;
      }

      .help-editor-header {
        width: 100%;
        z-index: 2;
        height: 50px;
      }
    `,
  ],
})
export class ConfigureHelpDialogComponent {
  public title!: Observable<string>;
  public help?: string;
  public enableHtmlMode = true;
  public HelpMode = HelpMode;
  public copyIcon = IoniconsOutline.copy;
  public codeIcon = IoniconsOutline.code;
  public textIcon = IoniconsOutline.text;

  public currentHelpMode: HelpMode | null = HelpMode.TOOLTIP;
  public changeModePending = false;
  public dialogEditorType = {
    wysiwyg: true,
    custom: false,
  };
  public formGroup: FormGroup = new FormGroup({
    help: new FormControl(this.help ? this.help : null),
  });
  public codeMirrorOptions = {
    theme: 'material',
    spellcheck: true,
    readOnly: false,
    height: 'auto',
    viewportMargin: Infinity,
    mode: {
      name: 'javascript',
      json: true,
      statementIndent: 2,
    },
    lineNumbers: false,
    lineWrapping: true,
    indentWithTabs: false,
    tabSize: 2,
  };
  quillModules: QuillModule = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ color: [] }, { background: [] }],
      [{ font: [] }],
      [{ align: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
    ],
  };
  private pendingModeToChange: HelpMode | null = null;

  constructor(public activeModal: NgbActiveModal) {}

  run(title: Observable<string>, help: string, enableHtmlMode: boolean) {
    this.title = title;
    this.help = help;
    this.enableHtmlMode = enableHtmlMode;
    if (this.help) {
      if (this.help.startsWith(DIALOG_MODE_IDENTIFIER) && enableHtmlMode) {
        this.currentHelpMode = HelpMode.DIALOG;
        this.dialogEditorType = {
          wysiwyg: true,
          custom: false,
        };
        this.formGroup.controls['help'].setValue(
          this.stripModeIdentifier(this.help)
        );
      } else {
        this.currentHelpMode = HelpMode.TOOLTIP;
        this.formGroup.controls['help'].setValue(this.help);
      }
    }
  }

  confirm() {
    const form: { help: string } = this.formGroup.getRawValue();
    if (form) {
      this.activeModal.close({
        help: this.enrichWithModeIdentifier(form.help),
      });
    } else {
      this.activeModal.close(false);
    }
  }
  onHelpModeChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value as HelpMode;
    this.helpModeChange(value);
  }

  private helpModeChange(helpMode: HelpMode) {
    this.changeModePending = true;
    this.pendingModeToChange = helpMode;
    this.dialogEditorType = {
      wysiwyg: true,
      custom: false,
    };
  }

  confirmSwitchMode() {
    this.formGroup.controls['help'].setValue(null);
    this.changeModePending = false;
    this.currentHelpMode = this.pendingModeToChange;
    this.pendingModeToChange = null;
  }

  abortSwitchMode() {
    this.changeModePending = false;
    switch (this.pendingModeToChange) {
      case HelpMode.DIALOG:
        this.currentHelpMode = HelpMode.TOOLTIP;
        break;
      case HelpMode.TOOLTIP:
        this.currentHelpMode = HelpMode.DIALOG;
        break;
    }
  }

  changeDialogEditorType(wysiwyg: boolean, custom: boolean) {
    this.dialogEditorType = {
      wysiwyg,
      custom,
    };
  }

  copyRawData() {
    let helpToCopy;
    const form: { help: string } = this.formGroup.getRawValue();
    if (form) {
      helpToCopy = this.enrichWithModeIdentifier(form.help);
      if (window.isSecureContext && navigator.clipboard) {
        navigator.clipboard.writeText(helpToCopy).then();
      } else {
        // Use the 'out of viewport hidden text area' trick
        const textArea = document.createElement('textarea');
        textArea.value = helpToCopy;

        // Move textarea out of the viewport in this case it's not visible
        textArea.style.position = 'absolute';
        textArea.style.left = '-999999px';

        document.body.prepend(textArea);
        textArea.select();

        try {
          document.execCommand('copy');
        } catch (error) {
          console.error(error);
        } finally {
          textArea.remove();
        }
      }
    }
  }

  private enrichWithModeIdentifier(help: string) {
    if (this.currentHelpMode === HelpMode.DIALOG) {
      return help ? `${DIALOG_MODE_IDENTIFIER}${help}` : '';
    } else {
      return help || '';
    }
  }

  private stripModeIdentifier(help: string) {
    return help.replace(DIALOG_MODE_IDENTIFIER, '');
  }
}
